import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { PipelineStage } from './LifecyclePipeline';
import { AnimatedCount } from './AnimatedCount';

interface MiniPipelineProps {
  loading: boolean;
  stages: PipelineStage[];
  className?: string;
  /**
   * Horizontal row (default, expanded sidebar) or vertical stack (collapsed
   * icon sidebar). Tooltips open to the side that won't cover the rail.
   */
  orientation?: 'horizontal' | 'vertical';
}

/**
 * Condensed lifecycle pipeline for the top header — one glyph per stage with a
 * corner badge that only surfaces when work is waiting, so the operational
 * backlog rides along on every page. Clicking a stage jumps to the same place
 * its full counterpart on Command does.
 */
export function MiniPipeline({
  loading,
  stages,
  className,
  orientation = 'horizontal',
}: MiniPipelineProps) {
  const vertical = orientation === 'vertical';

  return (
    <TooltipProvider delayDuration={120}>
      <div
        data-tour="work-queue"
        className={cn(
          'flex items-center gap-0.5 rounded-lg bg-white/[0.04] px-1 py-0.5 ring-1 ring-white/10',
          vertical && 'flex-col px-0.5 py-1',
          className,
        )}
      >
        {stages.map((stage) => (
          <MiniStage
            key={stage.id}
            stage={stage}
            loading={loading}
            tooltipSide={vertical ? 'right' : 'top'}
          />
        ))}
      </div>
    </TooltipProvider>
  );
}

function MiniStage({
  stage,
  loading,
  tooltipSide,
}: {
  stage: PipelineStage;
  loading: boolean;
  tooltipSide: 'top' | 'right';
}) {
  const Icon = stage.icon;
  const { count } = stage;
  const pending = loading || count === undefined;
  const hasWork = (count ?? 0) > 0;
  const isUrgent = !!stage.urgent && hasWork;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={stage.navigate}
          data-tour={`work-queue-${stage.id}`}
          aria-label={`${stage.title}: ${count ?? '—'}`}
          className={cn(
            // Named group so hover styles don't fire when the parent sidebar
            // `.group` is hovered (Tailwind group-hover matches any ancestor).
            'group/stage relative flex h-7 w-7 items-center justify-center rounded-md transition-colors duration-200',
            'hover:bg-white/[0.1] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/40',
          )}
        >
          <Icon
            className={cn(
              'h-4 w-4 transition-colors',
              isUrgent
                ? 'text-red-300'
                : hasWork
                ? 'text-white'
                : 'text-white/30 group-hover/stage:text-white/50',
            )}
            strokeWidth={2}
          />

          {/* Corner badge — only appears when there's something to act on. */}
          {pending ? (
            <span
              aria-hidden
              className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse rounded-full bg-white/25"
            />
          ) : hasWork ? (
            <span
              className={cn(
                'absolute -right-1 -top-1 flex h-[15px] min-w-[15px] items-center justify-center rounded-full px-1',
                'text-[9px] font-bold tabular-nums leading-none tracking-[-0.02em] ring-2 ring-nav',
                isUrgent ? 'bg-destructive text-white' : 'bg-white text-stone-900',
              )}
            >
              <AnimatedCount value={count} />
              {isUrgent && (
                <span
                  aria-hidden
                  className="absolute inset-0 -z-10 animate-ping rounded-full bg-destructive/60"
                />
              )}
            </span>
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide} className="flex items-center gap-2 px-2.5 py-1.5">
        <span className="text-xs font-semibold text-popover-foreground">{stage.title}</span>
        <span className="text-[11px] text-muted-foreground">
          {pending ? '…' : `${count!.toLocaleString()} · ${stage.verb}`}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}
